import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import SignaturePad from '@/components/SignaturePad';
import { fileUrl, api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { Check, X, FileSignature, DollarSign, Briefcase, Loader2, Sparkles } from 'lucide-react';

const statusBadge = (s) => {
  const m = {
    new: { label: 'New', cls: 'bg-blue-100 text-blue-700' },
    sent: { label: 'Sent', cls: 'bg-amber-100 text-amber-700' },
    approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-700' },
    declined: { label: 'Declined', cls: 'bg-red-100 text-red-700' },
    in_progress: { label: 'In Progress', cls: 'bg-indigo-100 text-indigo-700' },
    scheduled: { label: 'Scheduled', cls: 'bg-blue-100 text-blue-700' },
    completed: { label: 'Completed', cls: 'bg-emerald-100 text-emerald-700' },
    paid: { label: 'Paid', cls: 'bg-emerald-100 text-emerald-700' },
    overdue: { label: 'Overdue', cls: 'bg-red-100 text-red-700' },
    pending_signature: { label: 'Awaiting signature', cls: 'bg-amber-100 text-amber-700' },
    signed: { label: 'Signed', cls: 'bg-emerald-100 text-emerald-700' },
    pending_deposit: { label: 'Pending deposit', cls: 'bg-amber-100 text-amber-700' },
  };
  const x = m[s] || { label: s, cls: 'bg-slate-100 text-slate-700' };
  return <span className={`inline-flex text-[11px] font-semibold px-2 py-1 rounded-full ${x.cls}`}>{x.label}</span>;
};

export default function CustomerPortalPage() {
  const { user } = useAuth();
  const [estimates, setEstimates] = useState([]);
  const [formalEstimates, setFormalEstimates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [sig, setSig] = useState(null);
  const [signingId, setSigningId] = useState(null);
  const [loadingSign, setLoadingSign] = useState(false);
  const [payingDepositId, setPayingDepositId] = useState(null);

  const refresh = async () => {
    const [e, fe, j, i, c, b] = await Promise.all([
      api.get('/me/estimates'), api.get('/me/formal-estimates'),
      api.get('/me/jobs'), api.get('/me/invoices'),
      api.get('/me/contracts'), api.get('/me/bookings'),
    ]);
    setEstimates(e.data.estimates); setFormalEstimates(fe.data.estimates);
    setJobs(j.data.jobs); setInvoices(i.data.invoices);
    setContracts(c.data.contracts); setBookings(b.data.bookings);
  };
  useEffect(() => { refresh(); }, []);

  const approve = async (id) => { await api.post(`/me/estimates/${id}/approve`); toast.success('Estimate approved'); refresh(); };
  const decline = async (id) => { await api.post(`/me/estimates/${id}/decline`); toast.success('Estimate declined'); refresh(); };
  const approveFormal = async (id) => { await api.post(`/me/formal-estimates/${id}/approve`); toast.success('Estimate approved — pay deposit to lock in your spot'); refresh(); };
  const declineFormal = async (id) => { await api.post(`/me/formal-estimates/${id}/decline`); toast.success('Estimate declined'); refresh(); };
  const payEstimateDeposit = async (id) => {
    setPayingDepositId(id);
    try {
      const r = await api.post('/stripe/checkout', { purpose: 'estimate_deposit', estimate_id: id });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start checkout');
      setPayingDepositId(null);
    }
  };
  const payInvoice = async (id) => {
    try {
      const r = await api.post('/stripe/checkout', { purpose: 'invoice', invoice_id: id });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start checkout');
    }
  };
  const submitSignature = async () => {
    if (!sig) { toast.error('Please sign first'); return; }
    setLoadingSign(true);
    try { await api.post(`/me/contracts/${signingId}/sign`, { signature_data_url: sig }); toast.success('Contract signed!'); setSigningId(null); setSig(null); refresh(); }
    catch { toast.error('Signing failed'); } finally { setLoadingSign(false); }
  };

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#C8922F] font-semibold">My Portal</div>
            <h1 className="mt-1 font-headline text-3xl md:text-4xl text-[#17352D]">Welcome, {user?.name?.split(' ')[0]}</h1>
          </div>
          <Button asChild className="rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white"><Link to="/estimate"><Sparkles className="h-4 w-4 mr-2" /> New Estimate</Link></Button>
        </div>

        <Tabs defaultValue="estimates" className="w-full">
          <TabsList className="grid grid-cols-2 md:grid-cols-5 w-full bg-[#F2F7F5] rounded-2xl p-1">
            <TabsTrigger value="estimates" data-testid="portal-tab-estimates" className="rounded-xl">Estimates ({estimates.length + formalEstimates.length})</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="portal-tab-jobs" className="rounded-xl">Jobs ({jobs.length})</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="portal-tab-invoices" className="rounded-xl">Invoices ({invoices.length})</TabsTrigger>
            <TabsTrigger value="contracts" data-testid="portal-tab-contracts" className="rounded-xl">Contracts ({contracts.length})</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="portal-tab-bookings" className="rounded-xl">Bookings ({bookings.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="estimates" className="mt-6 space-y-4" data-testid="portal-estimates-list">
            {estimates.length === 0 && formalEstimates.length === 0 && <div className="text-slate-500">No estimates yet. <Link to="/estimate" className="text-[#17352D] underline">Request your first estimate</Link>.</div>}

            {/* Formal admin-built estimates (with line items + Pay Deposit) */}
            {formalEstimates.map((e) => {
              const deposit = Number(e.computed_deposit || 0);
              const canPay = (e.status === 'sent' || e.status === 'approved') && !e.deposit_paid && deposit >= 0.5;
              return (
                <div key={e.id} className="rounded-2xl border-2 border-[#C8922F]/40 bg-white p-5 shadow-sm" data-testid={`portal-formal-estimate-card-${e.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-[#C8922F] font-semibold">Formal Estimate · {e.estimate_number}</div>
                      <div className="font-headline text-xl text-[#17352D] mt-1">{e.service_name}</div>
                      <div className="text-sm text-slate-500 mt-0.5">{e.category_name} · {e.property_type} · {e.city || '—'}</div>
                    </div>
                    <div className="text-right">
                      {statusBadge(e.deposit_paid ? 'paid' : e.status)}
                      <div className="font-headline text-2xl text-[#17352D] mt-1">${Number(e.total).toLocaleString()}</div>
                    </div>
                  </div>

                  {e.lines?.length > 0 && (
                    <div className="mt-4 rounded-xl bg-[#F7F8FA] border border-[#E6E9EE] p-3 text-sm max-h-56 overflow-y-auto">
                      {e.lines.map((l, idx) => (
                        <div key={idx} className="flex justify-between py-1 border-b last:border-b-0 border-[#E6E9EE]">
                          <span className="text-slate-700">{l.description} <span className="text-xs text-slate-400">({l.type})</span></span>
                          <span className="text-slate-600 tabular-nums">{l.qty} × ${Number(l.unit_price).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div><div className="text-slate-400">Subtotal</div><div className="text-[#17352D] font-semibold">${Number(e.subtotal || 0).toLocaleString()}</div></div>
                    <div><div className="text-slate-400">Tax ({e.tax_pct}%)</div><div className="text-[#17352D] font-semibold">${Number(e.tax_amount || 0).toLocaleString()}</div></div>
                    <div><div className="text-slate-400">Total</div><div className="text-[#17352D] font-semibold">${Number(e.total || 0).toLocaleString()}</div></div>
                    <div><div className="text-slate-400">Deposit (50%)</div><div className="text-[#C8922F] font-semibold">${deposit.toLocaleString()}</div></div>
                  </div>

                  {e.notes && <div className="mt-3 text-xs text-slate-600 italic">Notes: {e.notes}</div>}

                  <div className="mt-4 flex flex-wrap gap-2">
                    {e.status === 'sent' && !e.deposit_paid && (
                      <>
                        <Button onClick={() => approveFormal(e.id)} data-testid={`portal-formal-approve-${e.id}`} className="rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white"><Check className="h-4 w-4 mr-1" /> Approve</Button>
                        <Button onClick={() => declineFormal(e.id)} variant="outline" data-testid={`portal-formal-decline-${e.id}`} className="rounded-xl"><X className="h-4 w-4 mr-1" /> Decline</Button>
                      </>
                    )}
                    {canPay && (
                      <Button
                        onClick={() => payEstimateDeposit(e.id)}
                        disabled={payingDepositId === e.id}
                        data-testid={`portal-pay-deposit-${e.id}`}
                        className="rounded-xl bg-[#C8922F] hover:bg-[#A87424] text-white"
                      >
                        {payingDepositId === e.id ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <DollarSign className="h-4 w-4 mr-1" />}
                        Pay 50% Deposit — ${deposit.toLocaleString()}
                      </Button>
                    )}
                    {e.deposit_paid && (
                      <div className="text-sm text-emerald-700 font-semibold inline-flex items-center gap-1"><Check className="h-4 w-4" /> Deposit paid — we&apos;ll be in touch to schedule</div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Wizard-submitted request estimates (with AI ballpark) */}
            {estimates.map((e) => (
              <div key={e.id} className="rounded-2xl border border-[#E6E9EE] bg-white p-5" data-testid={`portal-estimate-card-${e.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-headline text-xl text-[#17352D]">{e.sub_service_name}</div>
                    <div className="text-sm text-slate-500 mt-0.5">{e.category_name} · {e.property_type} · {e.city || '—'}</div>
                  </div>
                  {statusBadge(e.status)}
                </div>
                {e.ai_estimate && (
                  <div className="mt-3 rounded-xl bg-[#FBF7EE] border border-[#EED7A6] p-3 text-sm">
                    <div className="font-semibold text-[#17352D]">Ballpark: ${Number(e.ai_estimate.total_low).toLocaleString()} – ${Number(e.ai_estimate.total_high).toLocaleString()}</div>
                    <div className="text-slate-600">{e.ai_estimate.duration_days_low}–{e.ai_estimate.duration_days_high} days</div>
                  </div>
                )}
                {e.photos?.length > 0 && (
                  <div className="mt-3 flex gap-2 overflow-x-auto">
                    {e.photos.map((p) => <img key={p.path} src={fileUrl(p.path)} alt="photo" className="h-16 w-16 rounded-lg object-cover border border-[#E6E9EE]" />)}
                  </div>
                )}
                {e.status === 'new' || e.status === 'sent' ? (
                  <div className="mt-4 flex gap-2">
                    <Button onClick={() => approve(e.id)} data-testid={`portal-approve-${e.id}`} className="rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white"><Check className="h-4 w-4 mr-1" /> Approve</Button>
                    <Button onClick={() => decline(e.id)} variant="outline" data-testid={`portal-decline-${e.id}`} className="rounded-xl"><X className="h-4 w-4 mr-1" /> Decline</Button>
                  </div>
                ) : null}
              </div>
            ))}
          </TabsContent>

          <TabsContent value="jobs" className="mt-6 space-y-4" data-testid="portal-jobs-list">
            {jobs.length === 0 && <div className="text-slate-500">No jobs yet.</div>}
            {jobs.map((j) => (
              <div key={j.id} className="rounded-2xl border border-[#E6E9EE] bg-white p-5" data-testid={`portal-job-card-${j.id}`}>
                <div className="flex items-start justify-between"><div><div className="font-headline text-xl text-[#17352D]">{j.title}</div><div className="text-sm text-slate-500">{j.service_name}</div></div>{statusBadge(j.status)}</div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-5 gap-2">
                  {(j.milestones || []).map((m, idx) => (
                    <div key={idx} className={`rounded-lg border p-2 text-xs text-center ${m.done ? 'bg-[#F2F7F5] border-[#C7DED4] text-[#17352D] font-semibold' : 'bg-white border-[#E6E9EE] text-slate-500'}`}>
                      {m.done ? <Check className="h-3.5 w-3.5 inline mr-1" /> : null}{m.name}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="invoices" className="mt-6 space-y-4" data-testid="portal-invoices-list">
            {invoices.length === 0 && <div className="text-slate-500">No invoices yet.</div>}
            {invoices.map((i) => (
              <div key={i.id} className="rounded-2xl border border-[#E6E9EE] bg-white p-5" data-testid={`portal-invoice-card-${i.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div><div className="font-headline text-xl text-[#17352D]">{i.title}</div><div className="text-sm text-slate-500">{i.invoice_number} · ${Number(i.amount).toLocaleString()}</div></div>
                  {statusBadge(i.status)}
                </div>
                {i.status !== 'paid' && (
                  <Button onClick={() => payInvoice(i.id)} data-testid={`portal-pay-${i.id}`} className="mt-4 rounded-xl bg-[#C8922F] hover:bg-[#A87424] text-white"><DollarSign className="h-4 w-4 mr-1" /> Pay ${Number(i.amount).toLocaleString()}</Button>
                )}
              </div>
            ))}
          </TabsContent>

          <TabsContent value="contracts" className="mt-6 space-y-4" data-testid="portal-contracts-list">
            {contracts.length === 0 && <div className="text-slate-500">No contracts yet.</div>}
            {contracts.map((c) => (
              <div key={c.id} className="rounded-2xl border border-[#E6E9EE] bg-white p-5" data-testid={`portal-contract-card-${c.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div><div className="font-headline text-xl text-[#17352D]">{c.title}</div>{c.total_amount && <div className="text-sm text-slate-500">Total: ${Number(c.total_amount).toLocaleString()}</div>}</div>
                  {statusBadge(c.status)}
                </div>
                <div className="mt-3 text-sm text-slate-700 max-h-48 overflow-y-auto rounded-lg bg-[#F7F8FA] p-3 whitespace-pre-wrap">{c.body_text}</div>
                {c.status === 'pending_signature' && (
                  <Dialog open={signingId === c.id} onOpenChange={(o) => { if (!o) { setSigningId(null); setSig(null); } }}>
                    <DialogTrigger asChild>
                      <Button onClick={() => setSigningId(c.id)} data-testid={`portal-sign-${c.id}`} className="mt-4 rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white"><FileSignature className="h-4 w-4 mr-1" /> Sign Contract</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl"><DialogHeader><DialogTitle>Sign {c.title}</DialogTitle></DialogHeader>
                      <div className="text-sm text-slate-600 mb-2">Draw your signature below. By signing, you agree to the terms above.</div>
                      <SignaturePad onChange={setSig} />
                      <DialogFooter><Button onClick={submitSignature} disabled={loadingSign || !sig} data-testid="portal-sign-submit" className="rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white">{loadingSign ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit signature'}</Button></DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
                {c.signed_at && c.signature_data_url && (
                  <div className="mt-3 border-t border-[#E6E9EE] pt-3 text-xs text-slate-500">Signed by {c.signed_by} on {new Date(c.signed_at).toLocaleString()}<img src={c.signature_data_url} alt="signature" className="mt-2 max-h-24 border border-[#E6E9EE] rounded bg-white" /></div>
                )}
              </div>
            ))}
          </TabsContent>

          <TabsContent value="bookings" className="mt-6 space-y-4" data-testid="portal-bookings-list">
            {bookings.length === 0 && <div className="text-slate-500">No bookings yet. <Link to="/booking" className="text-[#17352D] underline">Book a service</Link>.</div>}
            {bookings.map((b) => (
              <div key={b.id} className="rounded-2xl border border-[#E6E9EE] bg-white p-5">
                <div className="flex items-start justify-between gap-3">
                  <div><div className="font-headline text-xl text-[#17352D]">{b.sub_service_name}</div><div className="text-sm text-slate-500">{b.date} @ {b.time_slot} · {b.city || '—'}</div></div>
                  {statusBadge(b.status)}
                </div>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </section>
    </Layout>
  );
}
